export const criticalThinkingCourse = {
  id: "critical-thinking-2024",
  title: "Critical Thinking: Logic, Reasoning & Decision Making",
  shortDescription: "Develop analytical thinking skills, logical reasoning abilities, and effective decision-making strategies.",
  description: "Comprehensive course in critical thinking covering logical reasoning, argument analysis, cognitive biases, and problem-solving strategies. Students will learn to evaluate information, identify fallacies, and make well-reasoned decisions in academic, professional, and personal contexts.",
  category: "Arts & Humanities",
  industryCategory: "Arts & Humanities",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from philosophy professors and logic specialists",
  thumbnailUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand principles of logical reasoning and argument structure",
    "Identify and analyze different types of logical fallacies",
    "Evaluate the credibility and reliability of information sources",
    "Apply critical thinking skills to problem-solving situations",
    "Recognize and overcome cognitive biases in decision-making",
    "Construct well-reasoned arguments and counterarguments",
    "Analyze complex issues from multiple perspectives",
    "Make evidence-based decisions in various contexts"
  ],

  prerequisites: [
    "High school education",
    "Basic reading comprehension",
    "Willingness to question assumptions and think analytically"
  ],

  skills: [
    "Logical Reasoning",
    "Argument Analysis",
    "Problem Solving",
    "Decision Making",
    "Information Evaluation",
    "Bias Recognition",
    "Analytical Writing",
    "Perspective Taking"
  ],

  marketData: {
    averageSalary: 58000,
    salaryRange: {
      min: 40000,
      max: 80000
    },
    jobDemand: 4.1,
    marketTrend: "growing",
    careerPaths: [
      "Policy Analyst",
      "Management Consultant",
      "Research Analyst",
      "Quality Assurance Specialist",
      "Project Manager",
      "Education Specialist"
    ],
    relatedSkills: [
      "Critical Thinking",
      "Analysis",
      "Problem Solving",
      "Research",
      "Communication",
      "Decision Making"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "logical-reasoning",
      title: "Foundations of Logical Reasoning",
      description: "Basic principles of logic, argument structure, and reasoning patterns",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "logic-intro",
          title: "Introduction to Logic and Reasoning",
          description: "What is critical thinking and why is it important?",
          type: "video",
          duration: 30
        },
        {
          id: "argument-structure",
          title: "Argument Structure and Analysis",
          description: "Understanding premises, conclusions, and logical relationships",
          type: "text",
          duration: 45,
          textContent: `# Argument Structure and Analysis

## Understanding Arguments

### What is an Argument?

In logic and critical thinking, an argument is a set of statements where one or more statements (premises) are offered as evidence or reasons for another statement (the conclusion).

**Components of an Argument:**
- **Premises**: Statements that provide evidence or reasons
- **Conclusion**: The statement that the premises are meant to support
- **Inference**: The logical connection between premises and conclusion

### Types of Arguments

**Deductive Arguments**
- **Structure**: If premises are true, conclusion must be true
- **Validity**: Logical structure determines validity
- **Soundness**: Valid argument with true premises
- **Example**: All humans are mortal. Socrates is human. Therefore, Socrates is mortal.

**Inductive Arguments**
- **Structure**: Premises provide probable support for conclusion
- **Strength**: Degree to which premises support conclusion
- **Cogency**: Strong argument with true premises
- **Example**: Most observed swans are white. Therefore, the next swan will probably be white.

### Identifying Arguments

**Argument Indicators**

**Premise Indicators:**
- Because, since, for, given that
- As evidenced by, for the reason that
- In light of, considering that

**Conclusion Indicators:**
- Therefore, thus, hence, so
- It follows that, we can conclude
- This shows that, consequently

**Non-Arguments**
- **Explanations**: Describe why something occurred
- **Descriptions**: Present factual information
- **Opinions**: Express personal views without reasons
- **Commands**: Give instructions or orders

### Argument Evaluation

**Evaluating Deductive Arguments**

**Validity Tests:**
1. Assume premises are true
2. Check if conclusion must follow
3. If conclusion can be false while premises are true, argument is invalid

**Common Valid Forms:**
- **Modus Ponens**: If P then Q; P; therefore Q
- **Modus Tollens**: If P then Q; not Q; therefore not P
- **Hypothetical Syllogism**: If P then Q; if Q then R; therefore if P then R

**Evaluating Inductive Arguments**

**Strength Criteria:**
- **Sample size**: Larger samples generally stronger
- **Representativeness**: Sample reflects target population
- **Diversity**: Multiple types of evidence
- **Absence of counterevidence**: No contradicting information

### Logical Fallacies

**Formal Fallacies**
Errors in logical structure:
- **Affirming the Consequent**: If P then Q; Q; therefore P
- **Denying the Antecedent**: If P then Q; not P; therefore not Q

**Informal Fallacies**
Errors in content or context:

**Ad Hominem**
- Attacking the person making the argument rather than the argument itself
- **Example**: "You can't trust John's economic policy because he's divorced"

**Straw Man**
- Misrepresenting someone's argument to make it easier to attack
- **Example**: "Senator X wants to reduce military spending, so he doesn't care about national security"

**False Dilemma**
- Presenting only two options when more exist
- **Example**: "Either we ban all guns or we accept mass shootings"

**Appeal to Authority**
- Citing irrelevant or unreliable authority
- **Example**: "This diet works because a celebrity endorses it"

**Slippery Slope**
- Claiming one event will lead to extreme consequences without justification
- **Example**: "If we allow gay marriage, next people will marry animals"

### Constructing Strong Arguments

**Steps for Argument Construction:**
1. **Clearly state your conclusion**
2. **Identify relevant premises**
3. **Ensure premises support conclusion**
4. **Consider counterarguments**
5. **Address potential objections**

**Characteristics of Strong Arguments:**
- **Clear and precise language**
- **Relevant and sufficient evidence**
- **Logical connection between premises and conclusion**
- **Acknowledgment of limitations and counterevidence**
- **Fair representation of opposing views**

### Argument Mapping

**Visual Representation**
Argument maps show the logical structure of complex arguments:
- **Convergent**: Multiple independent premises support conclusion
- **Linked**: Premises work together to support conclusion
- **Serial**: Chain of reasoning with intermediate conclusions

**Benefits of Argument Mapping:**
- Clarifies logical structure
- Identifies missing premises
- Reveals assumptions
- Helps evaluate argument strength

Understanding argument structure and evaluation is fundamental to critical thinking and helps us navigate complex issues with clarity and precision.`
        },
        {
          id: "logical-analysis",
          title: "Logical Analysis Exercise",
          description: "Practice identifying and evaluating arguments and fallacies",
          type: "arts-humanities-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "logical-analysis",
            instructions: "Analyze the following statements to identify arguments, premises, conclusions, and any logical fallacies.",
            examples: [
              {
                id: "example1",
                text: "We shouldn't listen to Professor Smith's views on climate change because he drives an SUV.",
                type: "Fallacy",
                analysis: "Ad hominem fallacy - attacking the person rather than addressing their arguments about climate change",
                fallacy: "Ad Hominem"
              },
              {
                id: "example2",
                text: "All students who study regularly get good grades. Maria studies regularly. Therefore, Maria will get good grades.",
                type: "Valid Argument",
                analysis: "Valid deductive argument with proper logical structure (modus ponens)",
                premises: ["All students who study regularly get good grades", "Maria studies regularly"],
                conclusion: "Maria will get good grades"
              }
            ],
            hints: [
              "Look for premise and conclusion indicators",
              "Consider whether the reasoning is logical",
              "Check if the argument attacks ideas or people"
            ]
          }
        }
      ],
      quiz: {
        id: "logical-reasoning-quiz",
        title: "Logical Reasoning Quiz",
        description: "Test your understanding of logical reasoning and argument analysis",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In a deductive argument, if the premises are true and the argument is valid, then:",
            options: [
              "The conclusion is probably true",
              "The conclusion must be true",
              "The conclusion might be false",
              "We need more information"
            ],
            correctAnswer: 1,
            explanation: "In a valid deductive argument, if the premises are true, the conclusion must be true by logical necessity."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which logical fallacy involves attacking the person making an argument rather than the argument itself?",
            options: [
              "Straw man",
              "False dilemma",
              "Ad hominem",
              "Slippery slope"
            ],
            correctAnswer: 2,
            explanation: "Ad hominem fallacy involves attacking the character or circumstances of the person making an argument rather than addressing the argument's merits."
          }
        ]
      }
    },
    {
      id: "cognitive-biases",
      title: "Cognitive Biases and Mental Shortcuts",
      description: "Understanding how cognitive biases affect reasoning and decision-making",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "bias-intro",
          title: "Introduction to Cognitive Biases",
          description: "What are cognitive biases and how do they influence thinking?",
          type: "video",
          duration: 35
        },
        {
          id: "common-biases",
          title: "Common Cognitive Biases and Heuristics",
          description: "Exploring confirmation bias, availability heuristic, anchoring, and other mental shortcuts",
          type: "text",
          duration: 50,
          textContent: `# Common Cognitive Biases and Heuristics

## Understanding Cognitive Biases

### What Are Cognitive Biases?

Cognitive biases are systematic errors in thinking that affect decisions and judgments. They represent deviations from rationality and optimal decision-making.

**Key Characteristics:**
- **Systematic**: Occur predictably in certain situations
- **Unconscious**: Often operate below awareness
- **Universal**: Affect all humans regardless of intelligence
- **Adaptive**: May have evolutionary advantages in some contexts

### Categories of Cognitive Biases

**Memory Biases**
Affect how we remember past events:
- **Availability Heuristic**: Judging probability by ease of recall
- **Recency Bias**: Overweighting recent information
- **Rosy Retrospection**: Remembering past events more positively

**Confirmation and Belief Biases**
Affect how we process information:
- **Confirmation Bias**: Seeking information that confirms existing beliefs
- **Belief Perseverance**: Maintaining beliefs despite contradictory evidence
- **Cherry Picking**: Selecting only supportive evidence

**Social and Attribution Biases**
Affect how we judge others:
- **Fundamental Attribution Error**: Overattributing behavior to personality
- **In-group Bias**: Favoring members of our own group
- **Halo Effect**: Letting one positive trait influence overall judgment

### Major Cognitive Biases

**Confirmation Bias**
- **Definition**: Tendency to search for, interpret, and recall information that confirms pre-existing beliefs
- **Examples**: 
  - Reading only news sources that align with political views
  - Interpreting ambiguous evidence as supporting preferred theory
- **Impact**: Leads to polarization, poor decision-making, resistance to change

**Availability Heuristic**
- **Definition**: Estimating probability based on how easily examples come to mind
- **Examples**:
  - Overestimating airplane crash risk after seeing news coverage
  - Judging crime rates based on media reports rather than statistics
- **Impact**: Systematic misjudgments about risk and frequency

**Anchoring Bias**
- **Definition**: Over-relying on first piece of information encountered
- **Examples**:
  - Real estate prices influenced by initial asking price
  - Salary negotiations starting from initial offer
- **Impact**: Inadequate adjustment from starting point

**Representativeness Heuristic**
- **Definition**: Judging probability by similarity to mental prototypes
- **Examples**:
  - Assuming someone is a librarian because they're quiet and bookish
  - Gambler's fallacy - expecting "due" outcomes in random events
- **Impact**: Ignoring base rates and statistical reasoning

**Overconfidence Bias**
- **Definition**: Overestimating one's own abilities, knowledge, or chances of success
- **Examples**:
  - Entrepreneurs overestimating business success probability
  - Students overconfident about exam performance
- **Impact**: Poor planning, inadequate preparation, excessive risk-taking

**Loss Aversion**
- **Definition**: Feeling losses more intensely than equivalent gains
- **Examples**:
  - Reluctance to sell losing investments
  - Preferring to avoid loss over acquiring gain
- **Impact**: Risk-averse behavior, status quo bias

**Sunk Cost Fallacy**
- **Definition**: Continuing investment based on previously invested resources
- **Examples**:
  - Staying in bad relationship because of time invested
  - Continuing failed project because of money already spent
- **Impact**: Throwing good money after bad, escalation of commitment

### Heuristics vs. Biases

**Heuristics as Mental Shortcuts**
- **Function**: Simplify complex decisions
- **Benefits**: Fast, efficient, often "good enough"
- **Problems**: Can lead to systematic errors

**When Heuristics Work Well**
- Familiar environments
- Time pressure situations
- Low-stakes decisions
- Pattern recognition tasks

**When Heuristics Fail**
- Novel situations
- Statistical reasoning required
- High-stakes decisions
- Counterintuitive problems

### Debiasing Strategies

**Awareness and Education**
- Learn about common biases
- Recognize bias-prone situations
- Practice identifying biases in thinking

**Systematic Decision Processes**
- Use structured decision frameworks
- Consider alternative explanations
- Seek disconfirming evidence
- Generate multiple options

**External Perspectives**
- Consult diverse viewpoints
- Use devil's advocate approach
- Seek feedback from others
- Consider outsider's view

**Statistical and Probabilistic Thinking**
- Use base rates and statistical data
- Consider sample sizes
- Think in terms of distributions
- Apply Bayesian reasoning

### Bias in Professional Contexts

**Medical Diagnosis**
- Anchoring on initial symptoms
- Confirmation bias in test interpretation
- Availability bias from recent cases

**Business Decision-Making**
- Overconfidence in forecasting
- Sunk cost fallacy in project management
- Groupthink in team decisions

**Legal Judgments**
- Anchoring on initial evidence
- Confirmation bias in case building
- Hindsight bias in evaluating decisions

Understanding cognitive biases is essential for improving decision-making, critical thinking, and rational analysis in both personal and professional contexts.`
        },
        {
          id: "bias-detection",
          title: "Bias Detection Exercise",
          description: "Practice identifying cognitive biases in real-world scenarios",
          type: "social-sciences-interactive",
          duration: 20,
          interactiveData: {
            exerciseType: "bias-identification",
            instructions: "Read each scenario and identify the primary cognitive bias being demonstrated.",
            scenarios: [
              {
                id: "scenario1",
                text: "After seeing several news reports about shark attacks, Maria cancels her beach vacation because she believes shark attacks are very common.",
                bias: "Availability Heuristic",
                explanation: "Maria is overestimating the probability of shark attacks because recent news coverage makes them easily recalled."
              },
              {
                id: "scenario2",
                text: "Despite losing money for three years, the company continues funding a failing project because they've already invested $2 million.",
                bias: "Sunk Cost Fallacy",
                explanation: "The company is making decisions based on past investments rather than future potential returns."
              }
            ]
          }
        }
      ],
      quiz: {
        id: "cognitive-biases-quiz",
        title: "Cognitive Biases Quiz",
        description: "Test your understanding of cognitive biases and mental shortcuts",
        timeLimit: 15,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Confirmation bias involves:",
            options: [
              "Seeking information that contradicts our beliefs",
              "Seeking information that confirms our existing beliefs",
              "Being overly confident in our abilities",
              "Anchoring on the first piece of information"
            ],
            correctAnswer: 1,
            explanation: "Confirmation bias is the tendency to search for, interpret, and recall information that confirms our pre-existing beliefs."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The availability heuristic leads us to:",
            options: [
              "Judge probability by how easily examples come to mind",
              "Stick with our first decision",
              "Overestimate our own abilities",
              "Follow what others are doing"
            ],
            correctAnswer: 0,
            explanation: "The availability heuristic causes us to estimate the probability of events based on how easily we can recall examples."
          }
        ]
      }
    },
    {
      id: "information-evaluation",
      title: "Information Evaluation and Source Credibility",
      description: "Learning to assess the reliability and credibility of information sources",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "source-evaluation",
          title: "Evaluating Information Sources",
          description: "How to assess the credibility and reliability of different information sources",
          type: "video",
          duration: 40
        },
        {
          id: "credibility-criteria",
          title: "Criteria for Source Credibility",
          description: "Standards and methods for evaluating information quality and reliability",
          type: "text",
          duration: 45,
          textContent: `# Criteria for Source Credibility

## Information Literacy in the Digital Age

### The Challenge of Information Overload

In today's digital environment, we face an unprecedented volume of information from diverse sources. The ability to evaluate information credibility has become a critical skill for:
- **Academic research**
- **Professional decision-making**
- **Civic participation**
- **Personal choices**

### Authority and Expertise

**Author Credentials**
- **Education**: Relevant degrees and training
- **Experience**: Professional background in the field
- **Affiliation**: Institutional connections and reputation
- **Recognition**: Awards, citations, peer acknowledgment

**Institutional Authority**
- **Academic institutions**: Universities and research centers
- **Professional organizations**: Medical associations, scientific societies
- **Government agencies**: FDA, CDC, Bureau of Labor Statistics
- **Established media**: Newspapers with editorial standards

**Expertise Indicators**
- **Specialization**: Focus on specific domain
- **Peer review**: Work evaluated by experts
- **Citations**: Referenced by other experts
- **Track record**: History of accurate information

### Accuracy and Reliability

**Fact-Checking Methods**
- **Primary sources**: Original research, firsthand accounts
- **Cross-referencing**: Verification with multiple sources
- **Update frequency**: Current and recently revised information
- **Error correction**: Willingness to acknowledge and fix mistakes

**Statistical and Data Quality**
- **Sample size**: Adequate for drawing conclusions
- **Methodology**: Appropriate research methods
- **Peer review**: Independent evaluation of research
- **Replication**: Results confirmed by other studies

**Red Flags for Inaccuracy**
- **Sensational claims** without evidence
- **Absolute statements** ("always," "never")
- **Outdated information** in rapidly changing fields
- **Lack of sources** or vague citations

### Objectivity and Bias

**Identifying Bias**
- **Financial interests**: Funding sources and conflicts of interest
- **Political agenda**: Partisan perspectives
- **Commercial motives**: Selling products or services
- **Personal beliefs**: Religious, ideological, or cultural biases

**Language and Presentation**
- **Emotional language**: Appeals to fear, anger, or excitement
- **Loaded terms**: Words with strong positive or negative connotations
- **Cherry-picking**: Selective presentation of evidence
- **False balance**: Giving equal weight to unequal evidence

**Balanced Sources**
- **Multiple perspectives**: Various viewpoints represented
- **Transparent methodology**: Clear about limitations
- **Acknowledges uncertainty**: Admits what isn't known
- **Considers counterarguments**: Addresses opposing views

### Currency and Timeliness

**Publication Date**
- **Recent information**: Current in rapidly changing fields
- **Historical context**: Understanding when information was created
- **Update frequency**: Regular revision and maintenance

**Field-Specific Considerations**
- **Technology**: Rapid obsolescence
- **Medicine**: Evolving treatments and guidelines
- **Law**: Changing regulations and precedents
- **History**: New discoveries and interpretations

### Purpose and Audience

**Information Purpose**
- **Educational**: Inform and teach
- **Commercial**: Sell products or services
- **Persuasive**: Change opinions or behavior
- **Entertainment**: Engage and amuse

**Target Audience**
- **General public**: Simplified explanations
- **Experts**: Technical detail and precision
- **Students**: Educational structure and support
- **Professionals**: Practical applications

### Digital Source Evaluation

**Website Assessment**
- **Domain authority**: .edu, .gov, .org vs. commercial sites
- **About page**: Information about organization and mission
- **Contact information**: Easy to reach and verify
- **Professional design**: Quality and maintenance indicators

**Social Media Considerations**
- **Verification**: Blue checkmarks and official accounts
- **Source linking**: Links to original sources
- **Viral content**: Popularity doesn't equal accuracy
- **Echo chambers**: Algorithm-driven confirmation bias

**Search Strategy**
- **Multiple sources**: Don't rely on single source
- **Diverse perspectives**: Seek various viewpoints
- **Primary sources**: Go to original information
- **Fact-checking sites**: Snopes, FactCheck.org, PolitiFact

### Scientific Information

**Peer Review Process**
- **Expert evaluation**: Reviewed by specialists
- **Methodology scrutiny**: Research methods evaluated
- **Reproducibility**: Results can be replicated
- **Publication standards**: Quality control measures

**Hierarchy of Evidence**
1. **Systematic reviews and meta-analyses**
2. **Randomized controlled trials**
3. **Cohort studies**
4. **Case-control studies**
5. **Case reports and expert opinion**

**Warning Signs**
- **Predatory journals**: Pay-to-publish schemes
- **Correlation vs. causation**: Misinterpreting relationships
- **Small sample sizes**: Insufficient for conclusions
- **Conflicts of interest**: Undisclosed financial ties

### News and Media Evaluation

**Editorial Standards**
- **Fact-checking**: Verification processes
- **Corrections policy**: How errors are handled
- **Source protection**: Journalistic ethics
- **Editorial independence**: Separation from advertising

**Media Literacy**
- **Headline accuracy**: Does headline match content?
- **Quote context**: Are quotes used appropriately?
- **Photo verification**: Are images authentic and relevant?
- **Source diversity**: Multiple viewpoints included?

### Practical Evaluation Framework

**CRAAP Test**
- **Currency**: Timeliness of information
- **Relevance**: Importance to your needs
- **Authority**: Source of information
- **Accuracy**: Reliability and correctness
- **Purpose**: Reason for information's existence

**Critical Questions**
1. Who created this information and why?
2. What evidence supports the claims?
3. How current is this information?
4. Are there alternative viewpoints?
5. Can this information be verified?

Developing strong information evaluation skills is essential for navigating our complex information environment and making well-informed decisions.`
        },
        {
          id: "source-analysis",
          title: "Source Analysis Practice",
          description: "Evaluate different types of sources for credibility and reliability",
          type: "arts-humanities-interactive",
          duration: 20,
          interactiveData: {
            exerciseType: "source-evaluation",
            instructions: "Evaluate each source using credibility criteria and determine its reliability for academic research.",
            sources: [
              {
                id: "source1",
                title: "Climate Change Effects on Agriculture",
                author: "Dr. Sarah Johnson, PhD in Environmental Science",
                publication: "Journal of Climate Research (peer-reviewed)",
                date: "2023",
                type: "Academic Article",
                credibilityScore: "High",
                reasoning: "Expert author, peer-reviewed journal, recent publication, academic institution affiliation"
              },
              {
                id: "source2",
                title: "Amazing Weight Loss Secret Doctors Don't Want You to Know!",
                author: "HealthGuru123",
                publication: "Personal blog",
                date: "Unknown",
                type: "Blog Post",
                credibilityScore: "Low",
                reasoning: "Anonymous author, sensational claims, no credentials, commercial motive"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "information-evaluation-quiz",
        title: "Information Evaluation Quiz",
        description: "Test your ability to evaluate information sources and credibility",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which is the strongest indicator of source credibility?",
            options: [
              "High website traffic",
              "Peer review by experts in the field",
              "Attractive website design",
              "Popular on social media"
            ],
            correctAnswer: 1,
            explanation: "Peer review by experts ensures that information has been evaluated by knowledgeable professionals for accuracy and quality."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is a red flag for potentially biased information?",
            options: [
              "Multiple sources cited",
              "Recent publication date",
              "Emotional language and sensational claims",
              "Author credentials provided"
            ],
            correctAnswer: 2,
            explanation: "Emotional language and sensational claims often indicate bias and attempts to persuade rather than inform objectively."
          }
        ]
      }
    },
    {
      id: "problem-solving",
      title: "Problem-Solving Strategies and Methods",
      description: "Systematic approaches to analyzing and solving complex problems",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "problem-solving-intro",
          title: "Introduction to Problem-Solving",
          description: "Overview of systematic problem-solving approaches and strategies",
          type: "video",
          duration: 35
        },
        {
          id: "problem-solving-methods",
          title: "Problem-Solving Methods and Frameworks",
          description: "Comprehensive guide to various problem-solving techniques and when to use them",
          type: "text",
          duration: 50,
          textContent: `# Problem-Solving Methods and Frameworks

## The Nature of Problems

### Types of Problems

**Well-Defined Problems**
- **Clear goal state**: Specific, measurable outcome
- **Known constraints**: Rules and limitations are explicit
- **Clear operators**: Steps to reach solution are evident
- **Examples**: Mathematical equations, puzzles, technical repairs

**Ill-Defined Problems**
- **Vague goal state**: Outcome not precisely specified
- **Unclear constraints**: Rules and limitations ambiguous
- **Multiple solutions**: Various acceptable approaches
- **Examples**: Career decisions, organizational change, creative projects

**Problem Complexity**
- **Simple problems**: Few variables, clear cause-effect
- **Complicated problems**: Many variables, but predictable
- **Complex problems**: Dynamic, emergent, unpredictable
- **Chaotic problems**: Rapidly changing, unclear patterns

### The Problem-Solving Process

**Generic Problem-Solving Model**
1. **Problem identification and definition**
2. **Information gathering and analysis**
3. **Solution generation**
4. **Solution evaluation and selection**
5. **Implementation planning**
6. **Execution and monitoring**
7. **Evaluation and learning**

## Problem Definition and Analysis

### Problem Definition Techniques

**5 Whys Technique**
- **Process**: Ask "why" five times to reach root cause
- **Example**:
  - Why are sales declining? → Customers are buying competitors' products
  - Why? → Our products are more expensive
  - Why? → Our costs are higher
  - Why? → Our manufacturing process is inefficient
  - Why? → We haven't updated equipment in 10 years

**Problem Statement Framework**
- **Current state**: What is happening now?
- **Desired state**: What should be happening?
- **Gap analysis**: What's the difference?
- **Constraints**: What limits our options?
- **Success criteria**: How will we know we've succeeded?

**Stakeholder Analysis**
- **Who is affected** by the problem?
- **Who has influence** over the solution?
- **What are their interests** and concerns?
- **How might they help** or hinder solutions?

### Information Gathering

**Data Collection Methods**
- **Quantitative data**: Numbers, statistics, measurements
- **Qualitative data**: Observations, interviews, surveys
- **Primary sources**: Direct observation and research
- **Secondary sources**: Existing reports and studies

**Analysis Techniques**
- **Root cause analysis**: Identifying underlying causes
- **SWOT analysis**: Strengths, weaknesses, opportunities, threats
- **Force field analysis**: Forces supporting vs. opposing change
- **Pareto analysis**: Identifying the most significant factors

## Solution Generation Methods

### Creative Thinking Techniques

**Brainstorming**
- **Rules**: No criticism, build on ideas, aim for quantity
- **Variations**: Silent brainstorming, electronic brainstorming
- **Benefits**: Generates many options, encourages creativity
- **Limitations**: May produce low-quality ideas, group conformity

**Mind Mapping**
- **Process**: Visual representation of ideas and connections
- **Benefits**: Non-linear thinking, reveals relationships
- **Applications**: Planning, note-taking, problem analysis

**SCAMPER Technique**
- **S**ubstitute: What can be substituted?
- **C**ombine: What can be combined?
- **A**dapt: What can be adapted?
- **M**odify: What can be modified?
- **P**ut to other uses: How else can this be used?
- **E**liminate: What can be removed?
- **R**everse: What can be rearranged?

### Analytical Problem-Solving

**Divide and Conquer**
- **Process**: Break complex problems into smaller parts
- **Benefits**: Makes problems manageable, allows parallel work
- **Applications**: Software debugging, project management

**Working Backwards**
- **Process**: Start with desired outcome, work to current state
- **Benefits**: Clarifies necessary steps, identifies obstacles
- **Applications**: Goal planning, mathematical proofs

**Analogical Reasoning**
- **Process**: Apply solutions from similar problems
- **Benefits**: Leverages existing knowledge, sparks creativity
- **Applications**: Design problems, strategic planning

## Decision-Making Frameworks

### Rational Decision-Making Model

**Steps**
1. **Define the problem** clearly
2. **Identify criteria** for solutions
3. **Weight the criteria** by importance
4. **Generate alternatives**
5. **Evaluate alternatives** against criteria
6. **Select best alternative**
7. **Implement and monitor**

**Decision Matrix**
- **Criteria listing**: All important factors
- **Weight assignment**: Relative importance scores
- **Alternative rating**: Score each option on each criterion
- **Calculation**: Weighted scores for comparison

### Bounded Rationality

**Satisficing**
- **Concept**: Choose "good enough" rather than optimal
- **When useful**: Time constraints, complex decisions
- **Process**: Set minimum acceptable criteria, choose first option that meets them

**Heuristic Decision-Making**
- **Recognition-primed decisions**: Use pattern recognition
- **Rule-based decisions**: Apply established procedures
- **Intuitive decisions**: Rely on experience and gut feelings

### Group Problem-Solving

**Nominal Group Technique**
- **Process**: Individual idea generation, group discussion, voting
- **Benefits**: Reduces groupthink, ensures participation
- **Applications**: Planning sessions, priority setting

**Delphi Method**
- **Process**: Anonymous expert opinions, iterative feedback
- **Benefits**: Avoids dominant personalities, builds consensus
- **Applications**: Forecasting, complex policy decisions

**Consensus Building**
- **Facilitated discussions**: Structured group conversations
- **Conflict resolution**: Addressing disagreements constructively
- **Compromise and negotiation**: Finding acceptable middle ground

## Implementation and Monitoring

### Implementation Planning

**Action Planning**
- **Specific tasks**: What exactly needs to be done?
- **Responsibilities**: Who will do each task?
- **Timelines**: When will tasks be completed?
- **Resources**: What's needed for success?

**Risk Assessment**
- **Identify potential problems**: What could go wrong?
- **Probability assessment**: How likely are problems?
- **Impact analysis**: How serious would problems be?
- **Mitigation strategies**: How can risks be reduced?

**Change Management**
- **Stakeholder communication**: Keeping people informed
- **Training and support**: Helping people adapt
- **Resistance management**: Addressing opposition
- **Culture alignment**: Ensuring fit with values

### Monitoring and Evaluation

**Key Performance Indicators**
- **Outcome measures**: Did we achieve the goal?
- **Process measures**: Did we follow the plan?
- **Leading indicators**: Early signs of success/failure
- **Lagging indicators**: Final results and impacts

**Continuous Improvement**
- **Regular reviews**: Scheduled assessment sessions
- **Feedback loops**: Learning from results
- **Adaptation**: Adjusting approach based on learning
- **Knowledge capture**: Documenting lessons learned

## Problem-Solving in Different Contexts

### Scientific Problem-Solving
- **Hypothesis formation**: Educated guesses about solutions
- **Experimental design**: Testing hypotheses systematically
- **Data analysis**: Interpreting results objectively
- **Peer review**: Validation by expert community

### Business Problem-Solving
- **Market analysis**: Understanding customer needs
- **Competitive analysis**: Learning from others' solutions
- **Cost-benefit analysis**: Evaluating financial implications
- **Stakeholder management**: Balancing different interests

### Personal Problem-Solving
- **Values clarification**: Understanding what matters most
- **Emotional regulation**: Managing feelings during decisions
- **Social support**: Getting help from others
- **Self-reflection**: Learning from past experiences

Effective problem-solving combines systematic approaches with creative thinking, adapting methods to fit the specific nature of each challenge.`
        },
        {
          id: "problem-solving-simulation",
          title: "Problem-Solving Simulation",
          description: "Practice applying problem-solving frameworks to realistic scenarios",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "problem-solving-practice",
            instructions: "Work through a complex problem using systematic problem-solving steps.",
            scenario: {
              title: "Declining Employee Morale",
              description: "A mid-size company has seen employee satisfaction scores drop from 8.2 to 6.1 over the past year. Turnover has increased 40%, and productivity metrics are declining.",
              challenge: "Identify the root causes and develop a comprehensive solution plan."
            },
            steps: [
              {
                step: "Problem Definition",
                prompt: "How would you clearly define this problem?",
                guidance: "Consider current state, desired state, and gap analysis"
              },
              {
                step: "Root Cause Analysis",
                prompt: "What might be causing the morale issues?",
                guidance: "Use the 5 Whys or other analytical techniques"
              },
              {
                step: "Solution Generation",
                prompt: "What potential solutions could address the root causes?",
                guidance: "Brainstorm multiple options before evaluating"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "problem-solving-quiz",
        title: "Problem-Solving Quiz",
        description: "Test your understanding of problem-solving methods and frameworks",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "The 5 Whys technique is primarily used for:",
            options: [
              "Generating creative solutions",
              "Identifying root causes",
              "Evaluating alternatives",
              "Building consensus"
            ],
            correctAnswer: 1,
            explanation: "The 5 Whys technique helps dig deeper into problems by repeatedly asking 'why' to identify underlying root causes."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is satisficing in decision-making?",
            options: [
              "Finding the optimal solution",
              "Choosing the first option that meets minimum criteria",
              "Using complex mathematical models",
              "Getting everyone to agree"
            ],
            correctAnswer: 1,
            explanation: "Satisficing involves choosing the first alternative that meets acceptable criteria rather than searching for the optimal solution."
          }
        ]
      }
    },
    {
      id: "decision-making",
      title: "Decision-Making Under Uncertainty",
      description: "Strategies for making effective decisions when facing incomplete information and uncertainty",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "uncertainty-intro",
          title: "Understanding Uncertainty and Risk",
          description: "Introduction to decision-making challenges in uncertain environments",
          type: "video",
          duration: 30
        },
        {
          id: "decision-frameworks",
          title: "Decision-Making Frameworks and Tools",
          description: "Comprehensive guide to structured decision-making approaches",
          type: "text",
          duration: 50,
          textContent: `# Decision-Making Frameworks and Tools

## Understanding Uncertainty

### Types of Uncertainty

**Risk vs. Uncertainty**
- **Risk**: Probabilities can be estimated (rolling dice, insurance actuarial tables)
- **Uncertainty**: Probabilities unknown or unknowable (market disruptions, technological breakthroughs)
- **Ambiguity**: Multiple interpretations possible (regulatory changes, competitor intentions)

**Sources of Uncertainty**
- **Information gaps**: Missing or incomplete data
- **Future events**: Unpredictable changes in environment
- **Human behavior**: Unpredictable actions of others
- **Complex systems**: Emergent properties and feedback loops

**Uncertainty Levels**
1. **Statistical uncertainty**: Clear variables, unknown probabilities
2. **Structural uncertainty**: Unclear relationships between variables
3. **Deep uncertainty**: Fundamental disagreement about problem structure

### Decision-Making Challenges

**Cognitive Limitations**
- **Bounded rationality**: Limited processing capacity
- **Information overload**: Too much data to process effectively
- **Time pressure**: Insufficient time for thorough analysis
- **Emotional influences**: Fear, excitement, and other emotions

**Environmental Complexity**
- **Dynamic conditions**: Rapidly changing situations
- **Interconnected systems**: Multiple interacting factors
- **Stakeholder diversity**: Various interests and perspectives
- **Resource constraints**: Limited time, money, and expertise

## Decision-Making Models

### Expected Value Analysis

**Basic Expected Value**
- **Formula**: EV = Σ (Probability × Outcome)
- **Application**: Choose option with highest expected value
- **Example**: Investment decisions with known probability distributions

**Decision Trees**
- **Structure**: Branching diagram showing decisions and outcomes
- **Components**: Decision nodes, chance nodes, outcome nodes
- **Benefits**: Visual representation, systematic analysis
- **Calculations**: Work backward from outcomes to decisions

**Sensitivity Analysis**
- **Purpose**: Test how results change with different assumptions
- **Method**: Vary key parameters and observe effects
- **Benefits**: Identifies critical assumptions, robust strategies

### Multi-Criteria Decision Analysis

**Analytic Hierarchy Process (AHP)**
- **Process**: Hierarchical breakdown of decision criteria
- **Pairwise comparisons**: Compare criteria and alternatives
- **Consistency checking**: Ensure logical coherence
- **Applications**: Complex decisions with multiple objectives

**TOPSIS Method**
- **Concept**: Choose alternative closest to ideal solution
- **Process**: Define ideal and anti-ideal solutions
- **Calculation**: Measure distance from ideal solutions
- **Benefits**: Handles conflicting criteria effectively

**Weighted Scoring Models**
- **Steps**: Identify criteria, assign weights, score alternatives
- **Benefits**: Transparent, systematic, easy to understand
- **Limitations**: Assumes linear relationships, equal intervals

### Robust Decision-Making

**Scenario Planning**
- **Purpose**: Explore multiple possible futures
- **Process**: Develop 3-4 plausible scenarios
- **Strategy**: Choose options that work across scenarios
- **Benefits**: Prepares for multiple contingencies

**Real Options Analysis**
- **Concept**: Preserve flexibility for future decisions
- **Types**: Option to expand, contract, abandon, switch
- **Valuation**: Use option pricing models
- **Applications**: R&D investments, market entry

**Minimax Regret**
- **Principle**: Minimize maximum regret across scenarios
- **Process**: Calculate regret for each decision-scenario combination
- **Choice**: Select decision with lowest maximum regret
- **Benefit**: Conservative approach to uncertainty

## Behavioral Decision-Making

### Prospect Theory

**Key Insights**
- **Reference point dependence**: Outcomes judged relative to reference
- **Loss aversion**: Losses feel worse than equivalent gains
- **Probability weighting**: Overweight small probabilities, underweight large ones
- **Framing effects**: Presentation affects choices

**Implications for Decision-Making**
- **Status quo bias**: Tendency to stick with current situation
- **Sunk cost fallacy**: Continuing based on past investments
- **Anchoring**: Over-relying on first information
- **Availability bias**: Overweighting easily recalled events

### Intuitive Decision-Making

**When Intuition Works**
- **High expertise**: Extensive domain knowledge
- **Pattern recognition**: Familiar problem types
- **Time pressure**: Quick decisions required
- **Complex situations**: Too many variables for analysis

**When to Use Analysis**
- **Novel situations**: Unfamiliar problems
- **High stakes**: Important consequences
- **Multiple stakeholders**: Need for transparency
- **Learning opportunities**: Want to improve future decisions

**Combining Intuition and Analysis**
- **Intuition first**: Generate options quickly
- **Analysis second**: Evaluate options systematically
- **Gut check**: Final intuitive review of analytical results
- **Learning loop**: Compare intuitive and analytical outcomes

## Group Decision-Making

### Group Decision Processes

**Consensus Building**
- **Full consensus**: Everyone agrees completely
- **Modified consensus**: Everyone can live with decision
- **Majority rule**: Decision by vote
- **Expert judgment**: Defer to most knowledgeable

**Structured Group Techniques**
- **Nominal group technique**: Individual input, group discussion
- **Delphi method**: Anonymous expert opinions
- **Dialectical inquiry**: Structured debate of alternatives
- **Devil's advocate**: Designated critic challenges ideas

### Group Decision Challenges

**Groupthink**
- **Symptoms**: Illusion of unanimity, self-censorship
- **Causes**: High cohesion, insulation, directive leadership
- **Prevention**: Encourage dissent, seek outside opinions

**Social Loafing**
- **Problem**: Individual effort decreases in groups
- **Solutions**: Clear individual accountability, smaller groups

**Polarization**
- **Phenomenon**: Groups make more extreme decisions than individuals
- **Causes**: Risk sharing, persuasive arguments
- **Management**: Structure discussions, consider individual views

## Decision Implementation

### Implementation Planning

**Action Planning**
- **Specific tasks**: Detailed breakdown of activities
- **Responsibility assignment**: Clear ownership of tasks
- **Timeline development**: Realistic schedules
- **Resource allocation**: Adequate support for implementation

**Change Management**
- **Stakeholder analysis**: Understanding impacts and reactions
- **Communication strategy**: Explaining rationale and benefits
- **Training and support**: Building capability for change
- **Resistance management**: Addressing opposition constructively

### Monitoring and Adaptation

**Performance Monitoring**
- **Key indicators**: Metrics that show progress
- **Regular reviews**: Scheduled assessment points
- **Early warning systems**: Indicators of potential problems
- **Feedback loops**: Learning from results

**Adaptive Management**
- **Experimentation**: Pilot programs and tests
- **Flexibility**: Ability to adjust course
- **Learning orientation**: Treating setbacks as information
- **Continuous improvement**: Ongoing refinement

## Decision-Making in Different Contexts

### Strategic Decision-Making
- **Long-term horizon**: Decisions with lasting impact
- **High uncertainty**: Unknown competitive responses
- **Multiple stakeholders**: Various interests to balance
- **Resource commitments**: Significant investments required

### Operational Decision-Making
- **Short-term focus**: Immediate problem solving
- **Clear procedures**: Established decision processes
- **Performance metrics**: Clear success measures
- **Routine problems**: Familiar decision situations

### Crisis Decision-Making
- **Time pressure**: Rapid response required
- **High stakes**: Significant consequences
- **Information gaps**: Incomplete information
- **Stress effects**: Emotional pressure on decision makers

### Ethical Decision-Making
- **Stakeholder analysis**: Who is affected?
- **Values clarification**: What principles apply?
- **Consequence evaluation**: What are the outcomes?
- **Universalizability**: Would this be acceptable as general rule?

Effective decision-making under uncertainty requires combining systematic analysis with recognition of human limitations and the dynamic nature of complex environments.`
        },
        {
          id: "decision-simulation",
          title: "Decision-Making Simulation",
          description: "Practice making decisions under uncertainty using structured frameworks",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "decision-analysis",
            instructions: "Use decision-making frameworks to analyze a complex business scenario.",
            scenario: {
              title: "Market Entry Decision",
              description: "Your company is considering entering a new international market. The market shows promise but involves significant risks and uncertainties.",
              options: [
                {
                  name: "Full Market Entry",
                  investment: "$10M",
                  timeframe: "18 months",
                  risks: "High regulatory risk, currency fluctuation"
                },
                {
                  name: "Joint Venture",
                  investment: "$5M",
                  timeframe: "12 months",
                  risks: "Partner dependency, shared control"
                },
                {
                  name: "Market Research Only",
                  investment: "$500K",
                  timeframe: "6 months",
                  risks: "Delayed entry, competitor advantage"
                }
              ]
            },
            frameworks: ["Expected Value Analysis", "Scenario Planning", "Real Options"]
          }
        }
      ],
      quiz: {
        id: "decision-making-quiz",
        title: "Decision-Making Quiz",
        description: "Test your understanding of decision-making under uncertainty",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main difference between risk and uncertainty?",
            options: [
              "Risk involves money, uncertainty involves time",
              "Risk has estimable probabilities, uncertainty does not",
              "Risk is emotional, uncertainty is logical",
              "Risk is short-term, uncertainty is long-term"
            ],
            correctAnswer: 1,
            explanation: "Risk involves situations where probabilities can be estimated, while uncertainty involves situations where probabilities are unknown or unknowable."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Scenario planning is most useful when:",
            options: [
              "Decisions have clear outcomes",
              "Future conditions are highly uncertain",
              "All stakeholders agree on goals",
              "Resources are unlimited"
            ],
            correctAnswer: 1,
            explanation: "Scenario planning helps prepare for multiple possible futures when uncertainty is high and we can't predict which scenario will occur."
          }
        ]
      }
    },
    {
      id: "argumentation",
      title: "Argumentation and Persuasion",
      description: "Constructing compelling arguments and understanding persuasive techniques",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "argumentation-intro",
          title: "The Art and Science of Argumentation",
          description: "Introduction to effective argumentation and persuasive communication",
          type: "video",
          duration: 35
        },
        {
          id: "argument-construction",
          title: "Constructing Effective Arguments",
          description: "Techniques for building persuasive and logically sound arguments",
          type: "text",
          duration: 45,
          textContent: `# Constructing Effective Arguments

## The Nature of Argumentation

### Argumentation vs. Argument

**Argumentation**
- **Collaborative process**: Working together to explore ideas
- **Truth-seeking**: Finding the best answer or solution
- **Constructive**: Building understanding through dialogue
- **Growth-oriented**: Learning and improving ideas

**Argument (confrontational)**
- **Competitive process**: Trying to win or dominate
- **Position-defending**: Protecting existing beliefs
- **Destructive**: Attacking people rather than ideas
- **Fixed**: Maintaining rigid positions

### Classical Rhetoric

**Aristotelian Appeals**

**Ethos (Credibility)**
- **Character**: Trustworthiness and integrity
- **Competence**: Expertise and knowledge
- **Goodwill**: Caring about audience's interests
- **Building ethos**: Cite credentials, show fairness, demonstrate preparation

**Pathos (Emotional Appeal)**
- **Emotional connection**: Engaging feelings and values
- **Motivational**: Inspiring action and commitment
- **Human element**: Making abstract concepts personal
- **Techniques**: Stories, imagery, metaphors, shared values

**Logos (Logical Appeal)**
- **Reasoning**: Sound logical structure
- **Evidence**: Supporting facts and data
- **Clarity**: Clear connections between ideas
- **Coherence**: Consistent and complete argument

### Toulmin Model of Argument

**Core Components**

**Claim**
- **Definition**: The conclusion or position being argued
- **Types**: Fact claims, value claims, policy claims
- **Characteristics**: Clear, specific, defensible

**Evidence (Data)**
- **Definition**: Information supporting the claim
- **Types**: Statistics, examples, testimony, research
- **Quality criteria**: Relevant, sufficient, credible, current

**Warrant**
- **Definition**: Logical connection between evidence and claim
- **Function**: Explains why evidence supports claim
- **Often implicit**: May need to be made explicit

**Advanced Components**

**Backing**
- **Definition**: Support for the warrant
- **Function**: Explains why the warrant is valid
- **Examples**: Principles, laws, precedents

**Qualifier**
- **Definition**: Degree of certainty in the claim
- **Terms**: "Probably," "likely," "in most cases"
- **Function**: Acknowledges limitations and uncertainty

**Rebuttal**
- **Definition**: Conditions under which claim might not hold
- **Function**: Addresses counterarguments and exceptions
- **Importance**: Shows awareness of complexity

## Argument Construction Strategies

### Claim Development

**Types of Claims**

**Factual Claims**
- **About existence**: "Climate change is occurring"
- **About causation**: "Smoking causes lung cancer"
- **About correlation**: "Education levels correlate with income"

**Value Claims**
- **About worth**: "Free speech is valuable"
- **About priorities**: "Security is more important than privacy"
- **About ethics**: "Animal testing is wrong"

**Policy Claims**
- **About action**: "We should increase minimum wage"
- **About solutions**: "Universal healthcare would reduce costs"
- **About regulations**: "Social media should be regulated"

**Claim Characteristics**
- **Specific**: Avoid vague generalizations
- **Defensible**: Can be supported with evidence
- **Significant**: Worth arguing about
- **Appropriate**: Fits audience and context

### Evidence Selection and Use

**Types of Evidence**

**Statistical Evidence**
- **Strengths**: Objective, quantifiable, generalizable
- **Weaknesses**: Can be manipulated, may lack context
- **Best practices**: Use recent data, cite sources, explain significance

**Expert Testimony**
- **Strengths**: Authoritative, credible, specialized knowledge
- **Weaknesses**: May be biased, credentials matter
- **Best practices**: Establish expertise, use multiple experts, note qualifications

**Examples and Cases**
- **Strengths**: Concrete, relatable, memorable
- **Weaknesses**: May not be representative, anecdotal
- **Best practices**: Use typical examples, acknowledge limitations

**Analogies**
- **Strengths**: Help explain complex ideas, persuasive
- **Weaknesses**: Similarities may be superficial
- **Best practices**: Ensure relevant similarities, acknowledge differences

**Evidence Quality Criteria**
- **Relevance**: Directly supports the claim
- **Sufficiency**: Adequate amount to support claim
- **Credibility**: From trustworthy sources
- **Currency**: Recent and up-to-date
- **Accuracy**: Factually correct
- **Representativeness**: Typical rather than exceptional

### Logical Structure

**Deductive Reasoning**
- **Structure**: General principle → specific application
- **Example**: All humans are mortal → Socrates is human → Socrates is mortal
- **Strength**: Provides certainty if premises are true
- **Application**: Legal reasoning, mathematical proofs

**Inductive Reasoning**
- **Structure**: Specific observations → general conclusion
- **Example**: Multiple studies show X → Therefore X is generally true
- **Strength**: Allows for discovery and generalization
- **Application**: Scientific research, market analysis

**Causal Reasoning**
- **Types**: Cause to effect, effect to cause
- **Criteria**: Temporal sequence, correlation, mechanism
- **Challenges**: Correlation vs. causation, multiple causes
- **Applications**: Policy arguments, problem-solving

## Persuasion Techniques

### Psychological Principles

**Reciprocity**
- **Principle**: People feel obligated to return favors
- **Application**: Acknowledge opponent's points before disagreeing
- **Example**: "You raise a valid concern about costs..."

**Consistency**
- **Principle**: People want to appear consistent
- **Application**: Connect new ideas to existing commitments
- **Example**: "Given your support for education..."

**Social Proof**
- **Principle**: People follow others' behavior
- **Application**: Show what similar people do
- **Example**: "Most professionals in your field..."

**Authority**
- **Principle**: People defer to experts
- **Application**: Cite credible authorities
- **Example**: "According to Nobel laureate..."

**Liking**
- **Principle**: People agree with those they like
- **Application**: Find common ground, show similarity
- **Example**: "As someone who also values..."

**Scarcity**
- **Principle**: People value rare opportunities
- **Application**: Emphasize unique aspects or urgency
- **Example**: "This window of opportunity..."

### Framing Strategies

**Gain vs. Loss Framing**
- **Gain frame**: "This will increase your income by 10%"
- **Loss frame**: "Not doing this costs you 10% of income"
- **Application**: Loss frames often more persuasive

**Reference Point Selection**
- **High anchor**: Start with ambitious proposal
- **Low anchor**: Start with minimal request
- **Strategic use**: Influences subsequent judgments

**Attribute Emphasis**
- **Highlight strengths**: Emphasize favorable characteristics
- **Minimize weaknesses**: Acknowledge but don't dwell
- **Reframe negatives**: Present disadvantages as trade-offs

## Addressing Counterarguments

### Anticipating Opposition

**Audience Analysis**
- **Values and beliefs**: What do they care about?
- **Knowledge level**: How much do they know?
- **Attitudes**: How do they feel about the topic?
- **Concerns**: What are their main objections?

**Common Objection Types**
- **Practical concerns**: Cost, feasibility, time
- **Value conflicts**: Different priorities or principles
- **Evidence disputes**: Disagreement about facts
- **Slippery slope**: Fear of unintended consequences

### Refutation Strategies

**Direct Refutation**
- **Address head-on**: Tackle objections directly
- **Provide counter-evidence**: Show opposing evidence is weak
- **Example**: "While critics claim X, recent studies show Y"

**Reframing**
- **Shift perspective**: Change how issue is viewed
- **Broaden context**: Consider larger picture
- **Example**: "Rather than seeing this as a cost, consider it an investment"

**Concession and Refutation**
- **Acknowledge validity**: Show you understand concerns
- **Provide alternative**: Offer different perspective
- **Example**: "While it's true that costs will increase, the benefits outweigh them because..."

**Turn the Tables**
- **Show consequences**: What happens without action?
- **Burden of proof**: Ask opponents to prove their position
- **Example**: "The real question isn't whether we can afford to act, but whether we can afford not to"

## Ethical Argumentation

### Principles of Ethical Argument

**Truthfulness**
- **Accurate representation**: Don't distort facts or sources
- **Complete picture**: Include relevant context
- **Acknowledge uncertainty**: Admit limitations in knowledge

**Fairness**
- **Represent opponents accurately**: Avoid straw man arguments
- **Consider multiple perspectives**: Show awareness of complexity
- **Respect persons**: Attack ideas, not people

**Transparency**
- **Reveal biases**: Acknowledge your own interests
- **Cite sources**: Give credit and allow verification
- **Show reasoning**: Make logical connections explicit

### Avoiding Manipulative Tactics

**Emotional Manipulation**
- **Fear-mongering**: Excessive use of fear appeals
- **Guilt-tripping**: Making audience feel guilty
- **False urgency**: Creating artificial time pressure

**Logical Fallacies**
- **Ad hominem**: Attacking the person
- **False dilemma**: Presenting only two options
- **Slippery slope**: Exaggerating consequences

**Information Distortion**
- **Cherry-picking**: Selecting only favorable evidence
- **Quote mining**: Using quotes out of context
- **False statistics**: Misrepresenting data

## Practical Applications

### Academic Writing**
- **Thesis statements**: Clear argumentative claims
- **Literature reviews**: Positioning within existing scholarship
- **Evidence integration**: Weaving sources into argument
- **Conclusion strength**: Reinforcing main argument

### Professional Communication**
- **Proposals**: Convincing stakeholders to support projects
- **Presentations**: Engaging and persuading audiences
- **Negotiations**: Finding mutually acceptable solutions
- **Change management**: Building support for initiatives

### Public Discourse**
- **Civic engagement**: Participating in democratic processes
- **Social media**: Constructive online discussions
- **Community organizing**: Building coalitions for change
- **Policy advocacy**: Influencing public policy

Effective argumentation combines logical rigor with psychological insight, ethical responsibility, and practical communication skills.`
        },
        {
          id: "argument-practice",
          title: "Argument Construction Practice",
          description: "Build and evaluate arguments using the Toulmin model and persuasion techniques",
          type: "arts-humanities-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "argument-building",
            instructions: "Construct a complete argument using the Toulmin model for the given scenario.",
            scenario: {
              topic: "Remote Work Policy",
              context: "Your company is considering a permanent remote work policy. Construct an argument either supporting or opposing this decision.",
              audience: "Company executives and HR department"
            },
            components: [
              {
                component: "Claim",
                prompt: "What is your main position on remote work policy?",
                guidance: "Make a clear, specific claim about what the company should do"
              },
              {
                component: "Evidence",
                prompt: "What evidence supports your claim?",
                guidance: "Include statistics, expert opinions, or case studies"
              },
              {
                component: "Warrant",
                prompt: "Why does your evidence support your claim?",
                guidance: "Explain the logical connection between evidence and claim"
              },
              {
                component: "Counterargument",
                prompt: "What would opponents argue?",
                guidance: "Acknowledge the strongest opposing position"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "argumentation-quiz",
        title: "Argumentation Quiz",
        description: "Test your understanding of argumentation and persuasion techniques",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In the Toulmin model, what connects the evidence to the claim?",
            options: [
              "Backing",
              "Qualifier",
              "Warrant",
              "Rebuttal"
            ],
            correctAnswer: 2,
            explanation: "The warrant is the logical connection that explains why the evidence supports the claim."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which of Aristotle's appeals focuses on the speaker's credibility?",
            options: [
              "Logos",
              "Pathos",
              "Ethos",
              "Kairos"
            ],
            correctAnswer: 2,
            explanation: "Ethos refers to the credibility and character of the speaker, including their expertise and trustworthiness."
          }
        ]
      }
    },
    {
      id: "analytical-writing",
      title: "Analytical Writing and Communication",
      description: "Developing clear, persuasive, and well-reasoned written communication",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "analytical-writing-intro",
          title: "Principles of Analytical Writing",
          description: "Introduction to effective analytical and critical writing techniques",
          type: "video",
          duration: 30
        },
        {
          id: "writing-process",
          title: "The Analytical Writing Process",
          description: "Step-by-step guide to planning, drafting, and revising analytical writing",
          type: "text",
          duration: 50,
          textContent: `# The Analytical Writing Process

## Understanding Analytical Writing

### What is Analytical Writing?

Analytical writing goes beyond description or summary to examine, interpret, and evaluate ideas, arguments, and evidence. It requires:

**Critical Thinking**
- **Analysis**: Breaking down complex ideas into components
- **Synthesis**: Combining ideas to create new understanding
- **Evaluation**: Making judgments about quality and validity
- **Interpretation**: Explaining meaning and significance

**Clear Communication**
- **Precision**: Accurate and specific language
- **Clarity**: Easy to understand and follow
- **Coherence**: Logical flow and organization
- **Conciseness**: Efficient use of words

### Types of Analytical Writing

**Academic Analysis**
- **Literary analysis**: Interpreting texts and themes
- **Research papers**: Evaluating sources and evidence
- **Case studies**: Examining specific situations
- **Critical reviews**: Assessing arguments and theories

**Professional Analysis**
- **Business reports**: Analyzing market trends and performance
- **Policy briefs**: Evaluating proposed solutions
- **Strategic plans**: Assessing options and recommendations
- **Technical documentation**: Explaining complex systems

**Public Writing**
- **Opinion pieces**: Arguing for specific positions
- **Investigative journalism**: Uncovering and explaining issues
- **Grant proposals**: Making cases for funding
- **White papers**: Providing authoritative analysis

## The Writing Process

### Pre-Writing Phase

**Understanding the Task**
- **Purpose**: Why are you writing? (inform, persuade, analyze)
- **Audience**: Who will read this? (experts, general public, decision-makers)
- **Scope**: What are the boundaries and expectations?
- **Context**: What's the situation and background?

**Research and Information Gathering**
- **Primary sources**: Original documents, data, interviews
- **Secondary sources**: Analysis and interpretation by others
- **Tertiary sources**: Reference materials and overviews
- **Source evaluation**: Credibility, relevance, currency

**Critical Reading Strategies**
- **Active reading**: Engaging with text, asking questions
- **Annotation**: Taking notes, highlighting key points
- **Summary**: Identifying main ideas and arguments
- **Analysis**: Examining assumptions, evidence, logic

### Planning and Organization

**Thesis Development**
- **Central argument**: Main claim or position
- **Specificity**: Clear and focused statement
- **Defensibility**: Can be supported with evidence
- **Significance**: Worth arguing or exploring

**Outline Creation**

**Introduction**
- **Hook**: Engaging opening
- **Context**: Background information
- **Thesis**: Central argument
- **Preview**: Overview of main points

**Body Paragraphs**
- **Topic sentences**: Main idea of each paragraph
- **Evidence**: Supporting information
- **Analysis**: Interpretation and explanation
- **Transitions**: Connections between ideas

**Conclusion**
- **Synthesis**: Bringing ideas together
- **Implications**: Broader significance
- **Future directions**: Next steps or questions

### Drafting Strategies

**Getting Started**
- **Free writing**: Write without stopping to edit
- **Outline expansion**: Develop outline into full paragraphs
- **Section drafting**: Work on one section at a time
- **Reverse outlining**: Create outline from existing draft

**Paragraph Development**

**PIE Structure**
- **Point**: Main idea (topic sentence)
- **Information**: Evidence and examples
- **Explanation**: Analysis and interpretation

**MEAL Structure**
- **Main idea**: Topic sentence
- **Evidence**: Supporting information
- **Analysis**: Interpretation
- **Link**: Connection to thesis or next paragraph

**Voice and Tone**
- **Academic voice**: Formal, objective, authoritative
- **Professional tone**: Clear, confident, respectful
- **Appropriate register**: Match language to audience
- **Consistency**: Maintain tone throughout

## Analytical Techniques

### Close Reading and Textual Analysis

**Literary Devices**
- **Metaphor and symbolism**: Figurative meanings
- **Irony and paradox**: Contradictions and tensions
- **Structure and form**: Organization and patterns
- **Character and setting**: Elements of narrative

**Rhetorical Analysis**
- **Ethos, pathos, logos**: Appeals to credibility, emotion, logic
- **Audience consideration**: Who is being addressed?
- **Purpose identification**: What is the goal?
- **Strategy evaluation**: How effective are the techniques?

### Data Analysis and Interpretation

**Quantitative Analysis**
- **Statistical significance**: Are differences meaningful?
- **Correlation vs. causation**: What relationships exist?
- **Trends and patterns**: What do the numbers show?
- **Limitations**: What are the constraints?

**Qualitative Analysis**
- **Theme identification**: What patterns emerge?
- **Content analysis**: What meanings are present?
- **Contextual interpretation**: What influences understanding?
- **Triangulation**: How do multiple sources compare?

### Comparative Analysis

**Comparison Strategies**
- **Point-by-point**: Compare each aspect across subjects
- **Block method**: Discuss each subject separately
- **Similarities and differences**: What do subjects share/differ?
- **Significance**: Why do comparisons matter?

**Evaluation Criteria**
- **Effectiveness**: Which approach works better?
- **Efficiency**: Which is more practical?
- **Ethics**: Which is more responsible?
- **Innovation**: Which is more creative?

## Revision and Editing

### Macro-Level Revision

**Argument Evaluation**
- **Thesis clarity**: Is the main argument clear?
- **Evidence sufficiency**: Is there enough support?
- **Logic coherence**: Do conclusions follow from premises?
- **Counter-argument consideration**: Are objections addressed?

**Organization Assessment**
- **Logical flow**: Do ideas connect smoothly?
- **Paragraph unity**: Does each paragraph have one main idea?
- **Transition effectiveness**: Are connections clear?
- **Conclusion strength**: Does ending reinforce argument?

### Micro-Level Editing

**Sentence-Level Issues**
- **Clarity**: Are sentences easy to understand?
- **Concision**: Are there unnecessary words?
- **Variety**: Is sentence structure varied?
- **Emphasis**: Are important ideas highlighted?

**Word Choice**
- **Precision**: Are words accurate and specific?
- **Tone appropriateness**: Do words fit the context?
- **Connotation**: What feelings do words evoke?
- **Technical language**: Is jargon appropriate for audience?

**Grammar and Mechanics**
- **Subject-verb agreement**: Do subjects and verbs match?
- **Pronoun reference**: Are pronouns clear?
- **Punctuation**: Are marks used correctly?
- **Spelling**: Are words spelled correctly?

## Evidence Integration

### Using Sources Effectively

**Quote Integration**
- **Signal phrases**: Introduce quotes with context
- **Quote sandwiching**: Explain significance after quotes
- **Quote accuracy**: Reproduce sources exactly
- **Quote selection**: Choose most relevant passages

**Paraphrasing**
- **Accurate representation**: Maintain original meaning
- **Your own words**: Don't copy source language
- **Attribution**: Credit original author
- **Integration**: Blend smoothly with your writing

**Summary**
- **Main points**: Capture essential ideas
- **Proportionality**: Reflect source's emphasis
- **Objectivity**: Avoid bias in representation
- **Attribution**: Credit source appropriately

### Citation and Documentation

**Citation Purposes**
- **Credit sources**: Acknowledge others' work
- **Support claims**: Provide evidence
- **Enable verification**: Allow readers to check
- **Avoid plagiarism**: Maintain academic integrity

**Citation Styles**
- **MLA**: Literature and humanities
- **APA**: Psychology and social sciences
- **Chicago**: History and some humanities
- **IEEE**: Engineering and technology

## Common Challenges and Solutions

### Writer's Block
- **Free writing**: Write without stopping
- **Change environment**: Find new writing space
- **Lower standards**: Focus on getting ideas down
- **Talk it out**: Discuss ideas with others

### Weak Arguments
- **More evidence**: Gather additional support
- **Counter-arguments**: Address opposing views
- **Clarity**: Make reasoning more explicit
- **Scope**: Narrow focus for stronger argument

### Poor Organization
- **Reverse outlining**: Create outline from draft
- **Topic sentences**: Strengthen paragraph focus
- **Transitions**: Improve connections
- **Road mapping**: Tell readers what's coming

### Unclear Writing
- **Active voice**: Use subject-verb-object structure
- **Concrete language**: Avoid abstract generalizations
- **Shorter sentences**: Break up complex ideas
- **Define terms**: Explain technical language

Effective analytical writing combines critical thinking with clear communication, requiring careful attention to both content and form.`
        },
        {
          id: "writing-workshop",
          title: "Analytical Writing Workshop",
          description: "Practice analytical writing techniques with guided exercises",
          type: "arts-humanities-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "writing-analysis",
            instructions: "Analyze and improve the provided writing samples using analytical writing principles.",
            samples: [
              {
                id: "sample1",
                title: "Weak Thesis Example",
                text: "Social media is bad for teenagers and causes many problems in their lives.",
                issues: ["Too vague", "No specific claim", "Not defensible with evidence"],
                improvement: "Excessive social media use (more than 3 hours daily) correlates with increased anxiety and depression among teenagers aged 13-17, suggesting the need for digital wellness education in schools."
              },
              {
                id: "sample2",
                title: "Poor Evidence Integration",
                text: "Studies show that exercise is good. 'Regular physical activity can reduce the risk of chronic disease by up to 50%' (Smith, 2023). This proves my point.",
                issues: ["Weak signal phrase", "No analysis of quote", "Unclear connection"],
                improvement: "According to Dr. Smith's comprehensive meta-analysis of 150 studies (2023), 'Regular physical activity can reduce the risk of chronic disease by up to 50%.' This substantial risk reduction demonstrates that exercise functions as a powerful preventive medicine, potentially saving millions in healthcare costs while improving quality of life."
              }
            ]
          }
        }
      ],
      quiz: {
        id: "analytical-writing-quiz",
        title: "Analytical Writing Quiz",
        description: "Test your understanding of analytical writing principles and techniques",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary purpose of a topic sentence?",
            options: [
              "To introduce a quote",
              "To state the main idea of the paragraph",
              "To provide evidence",
              "To conclude the argument"
            ],
            correctAnswer: 1,
            explanation: "A topic sentence states the main idea of a paragraph and helps readers understand what the paragraph will discuss."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "When integrating quotes, you should:",
            options: [
              "Let quotes speak for themselves",
              "Use signal phrases and explain significance",
              "Only use famous quotes",
              "Avoid quotes in analytical writing"
            ],
            correctAnswer: 1,
            explanation: "Effective quote integration requires signal phrases to introduce quotes and analysis to explain their significance to your argument."
          }
        ]
      }
    },
    {
      id: "applied-critical-thinking",
      title: "Applied Critical Thinking in Real-World Contexts",
      description: "Applying critical thinking skills to contemporary issues and professional scenarios",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "real-world-applications",
          title: "Critical Thinking in Practice",
          description: "How critical thinking applies to everyday decisions and professional challenges",
          type: "video",
          duration: 35
        },
        {
          id: "contemporary-applications",
          title: "Critical Thinking for Contemporary Challenges",
          description: "Applying critical thinking to modern issues like misinformation, technology, and global challenges",
          type: "text",
          duration: 45,
          textContent: `# Critical Thinking for Contemporary Challenges

## Information Age Challenges

### Digital Literacy and Misinformation

**The Information Landscape**
- **Volume**: Unprecedented amount of information available
- **Speed**: Instant global communication and sharing
- **Sources**: Traditional and new media, social platforms, user-generated content
- **Verification challenges**: Difficulty distinguishing reliable from unreliable information

**Types of Misinformation**

**Disinformation**
- **Definition**: Deliberately false information spread with intent to deceive
- **Examples**: Political propaganda, conspiracy theories, manufactured controversies
- **Motives**: Political gain, financial profit, social disruption
- **Characteristics**: Sophisticated, coordinated, targets emotions

**Misinformation**
- **Definition**: False information spread without malicious intent
- **Examples**: Misunderstood scientific studies, urban legends, well-meaning but incorrect advice
- **Spread**: Often through social networks, word-of-mouth
- **Characteristics**: May seem plausible, often contains partial truths

**Mal-information**
- **Definition**: True information used to cause harm
- **Examples**: Doxxing, revenge sharing, selective leaks
- **Context**: Information taken out of context or used inappropriately
- **Impact**: Can be as harmful as false information

**Detection Strategies**

**Source Verification**
- **Author credentials**: Check expertise and qualifications
- **Publication reputation**: Evaluate outlet's standards and history
- **Contact information**: Verify organization exists and is legitimate
- **Funding sources**: Understand potential conflicts of interest

**Content Analysis**
- **Fact-checking**: Use reliable fact-checking websites
- **Cross-referencing**: Compare with multiple sources
- **Date checking**: Ensure information is current
- **Context consideration**: Understand full story, not just headlines

**Technical Verification**
- **Reverse image search**: Check if images are authentic
- **Link verification**: Ensure links lead to legitimate sources
- **URL inspection**: Look for suspicious domain names
- **Social media verification**: Check for verified accounts

### Technology and AI Impact

**Algorithmic Decision-Making**

**Areas of Application**
- **Social media feeds**: What content we see
- **Search results**: Information we find
- **Hiring decisions**: Who gets interviewed
- **Credit scoring**: Financial opportunities
- **Criminal justice**: Sentencing and parole decisions

**Critical Evaluation**
- **Transparency**: Are algorithms explainable?
- **Bias**: Do algorithms discriminate unfairly?
- **Accuracy**: How often are decisions correct?
- **Accountability**: Who is responsible for outcomes?

**Deepfakes and Synthetic Media**
- **Technology**: AI-generated fake videos, audio, images
- **Detection challenges**: Increasingly sophisticated fakes
- **Implications**: Trust in evidence, political manipulation
- **Defense strategies**: Technical detection tools, media literacy

**Privacy and Surveillance**
- **Data collection**: What information is gathered?
- **Data use**: How is information utilized?
- **Consent**: Do people understand what they're agreeing to?
- **Protection**: What safeguards exist?

## Global and Social Issues

### Climate Change Communication

**Scientific Consensus**
- **Evidence base**: Multiple lines of evidence from different fields
- **Peer review**: Rigorous scientific evaluation process
- **Uncertainty**: Understanding confidence levels and limitations
- **Communication**: How uncertainty is presented to public

**Misinformation Tactics**
- **False balance**: Giving equal weight to fringe views
- **Cherry-picking**: Selecting only favorable data
- **Doubt manufacturing**: Creating false uncertainty
- **Ad hominem attacks**: Attacking scientists personally

**Critical Evaluation Skills**
- **Source expertise**: Is the source qualified in relevant field?
- **Methodology**: Are research methods appropriate?
- **Peer review**: Has work been evaluated by experts?
- **Replication**: Have results been confirmed by others?

### Political Discourse and Democracy

**Media Bias and Partisanship**

**Types of Bias**
- **Selection bias**: What stories are covered?
- **Framing bias**: How stories are presented?
- **Confirmation bias**: Seeking information that confirms beliefs?
- **Commercial bias**: How does funding affect coverage?

**Critical Media Consumption**
- **Diverse sources**: Read across political spectrum
- **Primary sources**: Go to original documents when possible
- **Fact-checking**: Verify claims independently
- **Context**: Understand historical and social background

**Polarization Challenges**
- **Echo chambers**: Reinforcement of existing beliefs
- **Filter bubbles**: Algorithmic content curation
- **Emotional reasoning**: Decisions based on feelings rather than facts
- **Tribal thinking**: In-group vs. out-group dynamics

### Economic Decision-Making

**Personal Finance**

**Investment Claims**
- **Risk assessment**: Understanding potential losses
- **Due diligence**: Researching investment opportunities
- **Expert credentials**: Evaluating financial advisors
- **Conflict of interests**: Understanding advisor incentives

**Consumer Decisions**
- **Marketing tactics**: Recognizing persuasion techniques
- **Product claims**: Evaluating effectiveness assertions
- **Price comparison**: Understanding total costs
- **Review reliability**: Assessing customer feedback authenticity

**Economic Policy**
- **Cause and effect**: Understanding economic relationships
- **Unintended consequences**: Considering indirect effects
- **Evidence base**: What research supports policies?
- **Stakeholder impacts**: Who benefits and who pays?

## Professional Applications

### Business and Management

**Strategic Decision-Making**

**Market Analysis**
- **Data quality**: Reliability of market research
- **Trend identification**: Distinguishing patterns from noise
- **Competitive intelligence**: Evaluating competitor information
- **Risk assessment**: Understanding potential downsides

**Performance Evaluation**
- **Metrics selection**: Are we measuring the right things?
- **Causation vs. correlation**: Understanding relationships
- **Benchmarking**: Appropriate comparison standards
- **Context consideration**: External factors affecting performance

**Innovation Management**
- **Idea evaluation**: Assessing feasibility and potential
- **Risk tolerance**: Balancing innovation with stability
- **Resource allocation**: Making investment decisions
- **Failure analysis**: Learning from unsuccessful projects

### Healthcare and Medicine

**Evidence-Based Practice**

**Research Evaluation**
- **Study design**: Are methods appropriate for questions?
- **Sample size**: Is study large enough for conclusions?
- **Peer review**: Has research been validated?
- **Replication**: Have results been confirmed?

**Treatment Decisions**
- **Risk-benefit analysis**: Weighing potential outcomes
- **Individual factors**: Considering patient-specific issues
- **Alternative options**: Exploring different approaches
- **Informed consent**: Ensuring patient understanding

**Health Information**
- **Source credibility**: Medical vs. non-medical sources
- **Anecdotal evidence**: Understanding limitations of personal stories
- **Commercial interests**: Identifying potential conflicts
- **Scientific consensus**: Understanding professional agreement

### Education and Learning

**Curriculum Development**
- **Learning objectives**: Clear, measurable goals
- **Evidence base**: Research-supported methods
- **Assessment validity**: Do tests measure what they claim?
- **Equity considerations**: Fair access and opportunity

**Student Assessment**
- **Multiple measures**: Using various assessment types
- **Bias recognition**: Understanding cultural and social factors
- **Growth vs. achievement**: Different ways to measure progress
- **Feedback quality**: Constructive and actionable information

## Practical Frameworks

### SWOT Analysis for Complex Issues

**Strengths**
- **Internal positive factors**: What advantages exist?
- **Capabilities**: What resources are available?
- **Competitive advantages**: What unique benefits?

**Weaknesses**
- **Internal limitations**: What constraints exist?
- **Skill gaps**: What capabilities are missing?
- **Resource limitations**: What's lacking?

**Opportunities**
- **External positive factors**: What favorable conditions exist?
- **Trends**: What developments could help?
- **Partnerships**: What collaborations are possible?

**Threats**
- **External challenges**: What obstacles exist?
- **Competition**: What alternative solutions compete?
- **Environmental changes**: What shifts could harm?

### Force Field Analysis

**Driving Forces**
- **Factors supporting change**: What pushes toward goals?
- **Stakeholder support**: Who favors the change?
- **Environmental pressures**: What external factors help?

**Restraining Forces**
- **Factors opposing change**: What creates resistance?
- **Stakeholder opposition**: Who opposes the change?
- **Resource constraints**: What limitations exist?

**Strategy Development**
- **Strengthen drivers**: How to increase supporting forces?
- **Weaken restraints**: How to reduce opposing forces?
- **New forces**: What additional factors could help?

### Systems Thinking

**Components**
- **Elements**: Individual parts of the system
- **Relationships**: Connections between elements
- **Purpose**: Overall function or goal
- **Environment**: External context and influences

**Patterns**
- **Linear causation**: Direct cause-and-effect relationships
- **Circular causation**: Feedback loops and cycles
- **Unintended consequences**: Unexpected system responses
- **Leverage points**: Where small changes create big impacts

### Scenario Planning

**Scenario Development**
- **Key uncertainties**: What factors could change significantly?
- **Driving forces**: What influences these uncertainties?
- **Scenario construction**: Develop 3-4 plausible futures
- **Consistency checking**: Ensure scenarios make sense

**Strategy Testing**
- **Robustness**: How well do strategies work across scenarios?
- **Flexibility**: Can strategies adapt to different futures?
- **Early warning indicators**: What signals suggest scenario shifts?
- **Contingency planning**: What if scenarios occur?

Applying critical thinking to contemporary challenges requires adapting fundamental skills to new contexts while maintaining rigorous standards for evidence, logic, and reasoning.`
        },
        {
          id: "case-study-analysis",
          title: "Contemporary Case Study Analysis",
          description: "Apply critical thinking skills to analyze real-world contemporary issues",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "case-analysis",
            instructions: "Analyze the provided case study using critical thinking frameworks and tools.",
            caseStudy: {
              title: "Social Media Misinformation During Health Crisis",
              background: "During a recent health crisis, various claims about treatments and prevention methods spread rapidly on social media. Some were scientifically supported, others were misleading or false.",
              scenario: "As a public health communications specialist, you need to develop strategies to combat misinformation while maintaining public trust and promoting evidence-based information.",
              stakeholders: ["General Public", "Healthcare Professionals", "Social Media Platforms", "Government Agencies", "Media Organizations"],
              challenges: ["Information overload", "Competing sources", "Speed of misinformation spread", "Public skepticism", "Platform policies"]
            },
            analysisSteps: [
              {
                step: "Problem Definition",
                prompt: "How would you define the core problem?",
                framework: "Use 5 Whys or problem statement framework"
              },
              {
                step: "Stakeholder Analysis",
                prompt: "What are the interests and concerns of each stakeholder?",
                framework: "Consider power, influence, and impact"
              },
              {
                step: "Solution Development",
                prompt: "What strategies could address the misinformation problem?",
                framework: "Use brainstorming and evaluation criteria"
              },
              {
                step: "Implementation Planning",
                prompt: "How would you implement your chosen strategy?",
                framework: "Consider resources, timeline, and potential obstacles"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "applied-critical-thinking-quiz",
        title: "Applied Critical Thinking Quiz",
        description: "Test your ability to apply critical thinking to real-world scenarios",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "When evaluating online health information, what is the most important first step?",
            options: [
              "Check if the information is recent",
              "Verify the source's medical credentials and affiliations",
              "See how many people shared the information",
              "Check if the website looks professional"
            ],
            correctAnswer: 1,
            explanation: "Verifying the source's medical credentials and institutional affiliations is crucial for evaluating health information credibility."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In systems thinking, what are feedback loops?",
            options: [
              "Direct cause-and-effect relationships",
              "Circular patterns where effects influence causes",
              "Linear progression of events",
              "Random system behaviors"
            ],
            correctAnswer: 1,
            explanation: "Feedback loops are circular patterns where the effects of an action influence the original cause, creating ongoing cycles."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What is the main purpose of scenario planning?",
            options: [
              "To predict the future accurately",
              "To prepare for multiple possible futures",
              "To choose the best single strategy",
              "To eliminate uncertainty"
            ],
            correctAnswer: 1,
            explanation: "Scenario planning helps prepare for multiple possible futures rather than trying to predict one specific future."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "argument-analysis-project",
      title: "Critical Analysis of Contemporary Debate",
      description: "Analyze arguments on both sides of a contemporary issue using critical thinking principles",
      type: "project",
      dueWeeks: 10,
      maxScore: 100,
      instructions: "Choose a current controversial topic and analyze the main arguments on different sides. Identify premises, conclusions, logical structure, potential fallacies, and evaluate the strength of reasoning on each side.",
      submissionFormat: "pdf",
      resources: [
        "Argument analysis frameworks",
        "Logical fallacy reference guides",
        "Contemporary debate sources",
        "Critical thinking assessment rubrics"
      ]
    }
  ],

  finalExam: {
    id: "critical-thinking-final-exam",
    title: "Critical Thinking Final Examination",
    description: "Comprehensive examination covering logical reasoning, argument analysis, and critical thinking applications",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What is the main difference between deductive and inductive reasoning?",
        options: [
          "Deductive uses premises, inductive doesn't",
          "Deductive guarantees certainty, inductive provides probability",
          "Deductive is always valid, inductive is always invalid",
          "Deductive is used in science, inductive in philosophy"
        ],
        correctAnswer: 1,
        explanation: "Deductive reasoning, when valid, guarantees that if premises are true, the conclusion must be true. Inductive reasoning provides probable support for conclusions."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "Confirmation bias is:",
        options: [
          "Seeking information that confirms our existing beliefs",
          "Always confirming what authorities tell us",
          "The tendency to agree with everyone",
          "Confirming facts through multiple sources"
        ],
        correctAnswer: 0,
        explanation: "Confirmation bias is the tendency to search for, interpret, and recall information in ways that confirm our pre-existing beliefs or hypotheses."
      }
    ]
  },

  quizzes: []
};